import { getInputErrorMessage } from "@/Utils/getInputErrorMessage";
import { Input } from "@/components/GenericComponents/input";
import InfoPopup from "@/components/InfoPopup/InfoPopup";

import { ComponentProps } from "react";
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";

type SearchInputProps<T extends FieldValues> = ComponentProps<typeof Input> & {
  id?: string;
  label?: string;
  name: string;
  entityName?: string /** Used for error messages if label is not set */;
  setValue: UseFormSetValue<any>;
  control: Control<T>;
  required?: boolean;
  locked?: boolean;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  register?: UseFormRegister<any>;
  trigger?: UseFormTrigger<any>;
  errorObj?: FieldError;
  tooltip?: Omit<ComponentProps<typeof InfoPopup>, "width">;
};

export const SearchInput = <T extends FieldValues>(
  props: SearchInputProps<T>,
) => {
  const {
    id,
    label,
    required,
    name,
    rules,
    control,
    setValue,
    trigger,
    locked,
    onFocus,
    onBlur,
    onChange,
    initialValue,
    errorObj,
    entityName,
    tooltip,
  } = props;

  const errorMessage = getInputErrorMessage({
    errorType: errorObj?.type || "",
    label: label ?? entityName,
  });

  return (
    <div className="flex flex-col">
      <label
        htmlFor={id}
        className="font-dm-sans text-sm font-medium text-black empty:hidden"
      >
        {label}
        {label && required && " *"}
        {tooltip && (
          <InfoPopup
            width="350px"
            size={15}
            className="mb-1 ml-1 inline-flex align-bottom"
            {...tooltip}
          />
        )}
      </label>
      <Controller
        name={name as Path<T>}
        control={control}
        rules={{ ...rules, required }}
        render={({ field: { value } }) => (
          <Input
            dropdownStyle={{ maxHeight: 190 }}
            dropdownIcon
            disabled={locked}
            error={!!errorObj} // handle errors here unless overridden
            errorMessage={errorMessage}
            {...props}
            initialValue={value || initialValue || undefined}
            noInitialOption={!(value || initialValue)}
            onFocus={onFocus}
            onChange={(option) => {
              const { value } = option;
              if (value) {
                setValue(name, value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
              onChange?.(option);
            }}
            onBlur={() => {
              onBlur?.();
              trigger?.(name);
            }}
          />
        )}
      />
    </div>
  );
};

export default SearchInput;
